import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Briefcase, ShoppingCart, Wrench, Megaphone, Building2, Mail, Send, X, Loader2, UserPlus } from "lucide-react";
import Avatar from "@/components/Avatar";
import { SERVICE_STYLE, avatarOf } from "./IntranetDirectory";

const TEAM_TONE = { "Direction": "Direction", "Marketing & Communication": "Marketing", "Commerce & Réseau": "Commerce", "RH": "RH", "Achats & Finance": "Achats", "Qualité & QSE": "Qualité", "Support & IT": "Support" };
const CONTACT_ICON = { RH: [Briefcase, "bg-[#ede9fe] text-[#6d28d9]"], Achat: [ShoppingCart, "bg-[#ecfccb] text-[#3f6212]"], Maintenance: [Wrench, "bg-[#ffedd5] text-[#c2410c]"], Marketing: [Megaphone, "bg-[#fce7f3] text-[#be185d]"], Autre: [Building2, "bg-[#e0f2fe] text-[#0369a1]"] };
const rel = (d) => { if (!d) return ""; const days = Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 864e5)); return days === 0 ? "aujourd'hui" : days === 1 ? "hier" : days < 60 ? `il y a ${days} jours` : `il y a ${Math.floor(days / 30)} mois`; };

export function TeamsStrip({ teams = [], onExplore, onAll }) {
  return (
    <section data-testid="directory-teams">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base md:text-lg font-bold text-[#0b1b3a]">Nos équipes au quotidien</h2>
        <button onClick={onAll} data-testid="directory-teams-all" className="text-xs font-semibold text-[#1d4ed8] hover:underline flex items-center gap-1">Voir toutes les équipes <ArrowRight className="w-3.5 h-3.5"/></button>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1 snap-x">
        {teams.map(t => (
          <div key={t.label} className="snap-start shrink-0 w-56 bg-white border border-slate-200 rounded-lg p-4 flex flex-col" data-testid={`directory-team-${t.label}`}>
            <span className={`self-start px-2 py-0.5 rounded-full text-[10px] font-bold ${SERVICE_STYLE[TEAM_TONE[t.label]] || "bg-slate-100 text-slate-600"}`}>{t.label}</span>
            <div className="mt-2 text-2xl font-black text-[#0b1b3a] leading-none">{t.count}</div>
            <div className="text-xs text-slate-500">collaborateur{t.count > 1 ? "s" : ""}</div>
            <div className="mt-3 flex -space-x-2">
              {t.avatars.map(a => <div key={a.id} className="ring-2 ring-white rounded-full"><Avatar user={avatarOf(a)} size={30}/></div>)}
              {t.count > t.avatars.length && <div className="w-[30px] h-[30px] rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold grid place-items-center ring-2 ring-white">+{t.count - t.avatars.length}</div>}
            </div>
            <button onClick={() => onExplore(t.services)} data-testid={`directory-team-explore-${t.label}`} className="mt-3 text-xs font-bold text-[#1d4ed8] hover:underline flex items-center gap-1 self-start">Explorer <ArrowRight className="w-3 h-3"/></button>
          </div>
        ))}
      </div>
    </section>
  );
}

export function RecentJoiners({ recent = [], onOpen, onAll }) {
  return (
    <section className="bg-white border border-slate-200 rounded-lg p-4" data-testid="directory-recent">
      <div className="flex items-center justify-between mb-2">
        <h2 className="flex items-center gap-2 text-base font-bold text-[#0b1b3a]"><UserPlus className="w-5 h-5 text-[#1d4ed8]"/>Ils nous ont rejoints récemment</h2>
        <button onClick={onAll} data-testid="directory-recent-all" className="text-xs font-semibold text-[#1d4ed8] hover:underline flex items-center gap-1">Voir tout <ArrowRight className="w-3.5 h-3.5"/></button>
      </div>
      <div className="divide-y divide-slate-100">
        {recent.map(p => (
          <button key={p.id} onClick={() => onOpen(p)} data-testid={`directory-recent-${p.id}`} className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-slate-50 rounded-md px-1">
            <Avatar user={avatarOf(p)} size={36}/>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-[#0b1b3a] truncate">{p.name}</div>
              <div className="text-xs text-slate-500 truncate">{p.poste} · {p.corner || p.region}</div>
            </div>
            <span className="text-[11px] text-slate-400 shrink-0">{rel(p.date_embauche)}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

export function SupportContacts({ contacts = [], onOpen }) {
  const [ask, setAsk] = useState(null);
  return (
    <section data-testid="directory-contacts">
      <h2 className="text-base md:text-lg font-bold text-[#0b1b3a] mb-3">Contacter un service</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {contacts.map(c => {
          const [Icon, tone] = CONTACT_ICON[c.key] || [Building2, "bg-slate-100 text-slate-600"];
          return (
            <div key={c.key} className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col" data-testid={`directory-contact-${c.key}`}>
              <div className={`w-10 h-10 rounded-lg grid place-items-center ${tone}`}><Icon className="w-5 h-5"/></div>
              <div className="mt-2 text-sm font-bold text-[#0b1b3a]">{c.label}</div>
              <a href={`mailto:${c.email}`} className="mt-1 text-xs text-[#1d4ed8] hover:underline flex items-center gap-1 truncate"><Mail className="w-3 h-3 shrink-0"/>{c.email}</a>
              {c.responsable ? (
                <button onClick={() => onOpen?.(c.responsable)} className="mt-3 flex items-center gap-2 text-left" data-testid={`directory-contact-resp-${c.key}`}>
                  <Avatar user={avatarOf(c.responsable)} size={28}/>
                  <div className="min-w-0"><div className="text-xs font-semibold text-[#0b1b3a] truncate">{c.responsable.name}</div><div className="text-[10px] text-slate-400 truncate">{c.responsable.poste}</div></div>
                </button>
              ) : <div className="mt-3 text-[11px] text-slate-400">Responsable à définir</div>}
              <button onClick={() => setAsk(c)} data-testid={`directory-contact-ask-${c.key}`} className="mt-auto pt-3 text-xs font-bold text-white bg-[#1d4ed8] hover:bg-blue-800 rounded-lg py-2 flex items-center justify-center gap-1"><Send className="w-3.5 h-3.5"/>Faire une demande</button>
            </div>
          );
        })}
      </div>
      {ask && <SupportRequestModal service={ask} onClose={() => setAsk(null)}/>}
    </section>
  );
}

const inp = "w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#1d4ed8] focus:ring-2 focus:ring-blue-100";

function SupportRequestModal({ service, onClose }) {
  const [f, setF] = useState({ titre: "", description: "", priorite: "Normal" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.titre.trim()) return toast.error("Objet requis");
    setSaving(true);
    try {
      await api.post("/support-tickets", { source_type: "support_direct", support_service: service.key, support_person_id: service.responsable?.id || null, ...f });
      toast.success(`Demande envoyée au service ${service.label}`); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg" data-testid="support-request-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Demande au service {service.label}</h3>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-slate-100" data-testid="support-request-close"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-5 space-y-3">
          {service.responsable && <div className="flex items-center gap-2 text-xs text-slate-500"><Avatar user={avatarOf(service.responsable)} size={24}/>Sera transmise à <b className="text-slate-800">{service.responsable.name}</b></div>}
          <input className={inp} placeholder="Objet de la demande" value={f.titre} onChange={e => setF(x => ({ ...x, titre: e.target.value }))} data-testid="support-request-title"/>
          <textarea rows={5} className={inp} placeholder="Décrivez votre besoin…" value={f.description} onChange={e => setF(x => ({ ...x, description: e.target.value }))} data-testid="support-request-desc"/>
          <select className={inp} value={f.priorite} onChange={e => setF(x => ({ ...x, priorite: e.target.value }))} data-testid="support-request-priority">{["Normal", "Important", "Urgent"].map(p => <option key={p}>{p}</option>)}</select>
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
          <button onClick={submit} disabled={saving} data-testid="support-request-submit" className="px-4 py-2 rounded-lg bg-[#1d4ed8] hover:bg-blue-800 text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-60">{saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}Envoyer</button>
        </div>
      </div>
    </div>
  );
}
